import { apiFetch } from '../../lib/apiFetch'
import { isDevMock } from '../../dev/isDevMock'
import { ChatStreamError, type ChatFailureReason, type ChatTurnStats } from './postChat'
import type { ChatStreamEvent, ProjectChatRequestBody } from '../../../netlify/functions/lib/project-chat-types'
import type { ChatStreamHandlers } from './postChat'
import { mockChatTurn } from './postChat'

// Polling client for project chat. The turn runs in project-chat-background and is written to a row;
// this starts it and then reads it until it finishes — one request in flight at a time, the gap timed
// from the previous response rather than the previous request, and paced to how much is actually
// happening (fast while the reply moves, wider while it doesn't).
//
// It replaces a streaming reader whose failure mode was silence: the response body was cut after ~10-12
// seconds of quiet — which is every turn whose first move is a tool call, since nothing streams until
// the model emits text — and end-of-stream is indistinguishable from a clean finish, so the user got no
// reply and no error while the server went on to produce an answer nobody received (RQI-99).
//
// A poll has no equivalent hole. There is no connection to lose: a failed request is just a poll that
// didn't land, and the next one a second later sees everything it missed, because the turn's state
// lives in the database rather than in flight.

// Gap between polls, measured from the END of the previous one — never from its start. Exactly one
// request is in flight at any moment: the loop below is strictly sequential, so a slow poll delays the
// next one rather than stacking on top of it. Overlapping polls would be pure downside here, since a
// later response can only ever supersede an earlier one.
const POLL_GAP_MS = 300
// Hard ceiling on ONE poll. Without it a single hung request stalls the whole turn: single-flight means
// nothing else can run, and the deadline check only happens between polls. With it, a hang degrades
// into an ordinary retry.
const POLL_TIMEOUT_MS = 10_000
// Give up this long after the turn started. Comfortably past the server's own 2-minute budget, so in
// practice the server always wins the race and answers with a graceful "I ran out of time" reply —
// this only fires when the backend has stopped updating the row at all.
const CLIENT_DEADLINE_MS = 150_000
// How long polls may keep failing before the turn is called lost. Measured in TIME, not in attempts: at
// a 300ms cadence a fixed retry count is a few hundred milliseconds of tolerance, which would throw away
// an answer sitting complete in the database because a phone changed networks.
const FAILURE_GRACE_MS = 15_000
// Consecutive failures back off, so a struggling backend isn't polled three times a second. Resets the
// moment one succeeds.
const MAX_BACKOFF_MS = 2_000

// --- adaptive pacing ---
//
// 300ms is the right cadence while a reply is actually arriving, and waste while it isn't. A turn is
// silent for most of its length — every tool call is a gap with nothing to report — and at a flat 300ms
// a two-minute turn is ~400 polls, nearly all of them re-reading an unchanged row.
//
// So: poll fast while something is changing, widen while nothing is, and snap straight back the moment
// it changes again. The gap only grows after a short run of genuinely idle polls, so an ordinary pause
// between tokens doesn't trip it.
const IDLE_POLLS_BEFORE_BACKOFF = 3
// Ceiling on the idle gap. Deliberately the cadence this loop used to run at unconditionally, so the
// slowest this can ever be is the old behaviour — and that only while there is nothing to show. Higher
// would save a few more requests at the cost of a visible stall when output resumes, which is exactly
// the moment the user is waiting on.
const MAX_IDLE_GAP_MS = 1_000

interface StartResponse {
  turnId: string
  messageId: string
  status: string
  pollMs?: number
}

interface PollResponse {
  turnId: string
  status: 'queued' | 'running' | 'done' | 'error' | 'timeout'
  messageId: string
  text: string
  agentStatus: string | null
  error: string | null
  events: ChatStreamEvent[]
  cursor: number
  done: boolean
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

// Run one project-chat turn to completion. Resolves with the turn's stats when the user actually got a
// response; throws a ChatStreamError (the same type the streaming path throws, so the existing
// telemetry and failure-notice copy keep working unchanged) otherwise.
export async function runChatTurn(
  body: ProjectChatRequestBody,
  // onText is REQUIRED here (it is optional on the shared type, which the streaming transport also
  // uses): a poll reports the whole reply, not a delta, and routing that through the append-style
  // onDelta would duplicate the answer on every poll.
  handlers: ChatStreamHandlers & { onText: (id: string, fullText: string) => void },
): Promise<ChatTurnStats> {
  if (isDevMock()) return await mockChatTurn(body, handlers)

  const startedAt = Date.now()
  const stats: ChatTurnStats = {
    turnId: null, elapsedMs: 0, bytesReceived: 0, eventsReceived: 0, lastEventKind: null,
    sawDone: false, sawText: false, deltaChars: 0, sawUi: false,
  }
  const finish = (): ChatTurnStats => { stats.elapsedMs = Date.now() - startedAt; return stats }
  const fail = (reason: ChatFailureReason, message: string, httpStatus?: number): never => {
    throw new ChatStreamError(reason, message, finish(), httpStatus)
  }

  // --- start ---
  let start: StartResponse
  try {
    const res = await apiFetch('/api/project-chat-turn', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      let detail = ''
      try { detail = ((await res.json()) as { error?: string }).error ?? '' } catch { /* non-JSON */ }
      fail('http', detail || `Chat request failed (${res.status})`, res.status)
    }
    start = (await res.json()) as StartResponse
  } catch (e) {
    if (e instanceof ChatStreamError) throw e
    return fail('network', e instanceof Error ? e.message : 'Network request failed')
  }
  stats.turnId = start.turnId
  const gapMs = Math.max(100, start.pollMs ?? POLL_GAP_MS)

  // --- poll ---
  //
  // Sequential by construction: each iteration awaits its gap, then awaits its request, so the next
  // poll cannot begin until this one has resolved. One request in flight, always, and the gap is real
  // idle time rather than a rate that a slow network could outrun.
  let cursor = 0
  let lastText = ''
  let lastAgentStatus: string | null = null
  let consecutiveFailures = 0
  let firstFailureAt = 0
  // Polls in a row that reported nothing new. Drives the idle pacing above.
  let idlePolls = 0

  for (;;) {
    // Failing beats idle: a backend that is erroring should be left alone regardless of how interesting
    // the turn was a moment ago.
    const wait = consecutiveFailures > 0
      ? Math.min(gapMs * 2 ** consecutiveFailures, MAX_BACKOFF_MS)
      : idlePolls < IDLE_POLLS_BEFORE_BACKOFF
        ? gapMs
        : Math.min(gapMs * 2 ** (idlePolls - IDLE_POLLS_BEFORE_BACKOFF + 1), MAX_IDLE_GAP_MS)
    await sleep(wait)

    if (Date.now() - startedAt > CLIENT_DEADLINE_MS) {
      return fail('truncated', 'The reply did not finish in time.')
    }

    let poll: PollResponse
    try {
      const res = await apiFetch(
        `/api/project-chat-turn?turn_id=${encodeURIComponent(start.turnId)}&after=${cursor}`,
        { signal: AbortSignal.timeout(POLL_TIMEOUT_MS) },
      )
      // A 404 means the turn is gone for good (or was never ours) — no amount of retrying fixes it.
      if (res.status === 404) return fail('server', 'That reply is no longer available.', 404)
      if (!res.ok) throw new Error(`poll failed (${res.status})`)
      poll = (await res.json()) as PollResponse
      consecutiveFailures = 0
      firstFailureAt = 0
    } catch (e) {
      // Transient. The answer is in the database, not on this connection, so just try again — for as
      // long as the grace window allows, however many attempts that turns out to be.
      if (!firstFailureAt) firstFailureAt = Date.now()
      consecutiveFailures += 1
      if (Date.now() - firstFailureAt >= FAILURE_GRACE_MS) {
        return fail('network', e instanceof Error ? e.message : 'Lost contact with the QA assistant.')
      }
      continue
    }

    stats.eventsReceived += poll.events.length
    cursor = poll.cursor

    // Anything at all to show resets the pacing to its fastest. Checked BEFORE the handlers run so a
    // turn that has resumed after a quiet stretch is back at 300ms for the very next poll.
    const changed = poll.text !== lastText || poll.events.length > 0 || poll.agentStatus !== lastAgentStatus
    idlePolls = changed ? 0 : idlePolls + 1

    // Text is the whole reply so far, not a delta, so this is idempotent: a repeated or out-of-order
    // poll re-renders the same bubble rather than duplicating it.
    if (poll.text !== lastText) {
      lastText = poll.text
      stats.sawText = poll.text.length > 0
      stats.deltaChars = poll.text.length
      handlers.onText(poll.messageId, poll.text)
    }
    // Only on change — the status line is unchanged on most polls, and re-firing it every time made the
    // handler churn once per poll for no reason.
    if (poll.agentStatus !== lastAgentStatus) {
      lastAgentStatus = poll.agentStatus
      if (poll.agentStatus) handlers.onStatus?.(poll.agentStatus)
    }

    for (const event of poll.events) {
      stats.lastEventKind = event.kind
      dispatchEvent(event, handlers, stats)
    }

    if (poll.done) {
      stats.sawDone = poll.status === 'done'
      if (poll.status === 'timeout') return fail('truncated', poll.error || 'The reply did not finish in time.')
      if (poll.status === 'error') return fail('server', poll.error || 'Something went wrong answering that.')
      if (!stats.sawText && !stats.sawUi) return fail('empty', 'The assistant finished without saying anything.')
      return finish()
    }
  }
}

// Side-band events. Deliberately the same set the streaming reader dispatched, MINUS the two the server
// now applies itself: `settings` and `project_status` used to be instructions to the browser to go and
// make the change, which meant a lost event was a lost change. They are notifications now — the write
// has already happened — so they only tell the open UI to refresh.
function dispatchEvent(event: ChatStreamEvent, handlers: ChatStreamHandlers, stats: ChatTurnStats) {
  switch (event.kind) {
    case 'status': handlers.onStatus?.(event.status); break
    case 'form': stats.sawUi = true; handlers.onForm?.(event.form); break
    case 'choices': stats.sawUi = true; handlers.onChoices?.(event.choices); break
    case 'github': stats.sawUi = true; handlers.onGithub?.(event.focus ?? 'all', event.repoUrl, event.resumePrompt); break
    case 'issue_tracker': stats.sawUi = true; handlers.onIssueTracker?.(event.provider); break
    case 'summary_destination': stats.sawUi = true; handlers.onSummaryDestination?.(event.channel); break
    case 'settings': stats.sawUi = true; handlers.onSettings?.(event.patch, event.summary); break
    case 'project_status': stats.sawUi = true; handlers.onProjectStatus?.(event.status); break
    case 'refresh': stats.sawUi = true; handlers.onRefresh?.(); break
    default: break
  }
}
